import { useEffect, useRef } from "react";

/**
 * Infinite scroll sentinel — uses the nearest scroll pane as root when present
 * (group detail, home feed column), otherwise the viewport.
 */
export function useInfiniteScroll({
  enabled = true,
  hasNextPage = false,
  isLoading = false,
  isLoadingMore = false,
  onLoadMore,
  sentinelRef,
  scrollRootSelectors = [".group-page__main", ".home-page__feed-column"],
}) {
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  const busyRef = useRef(false);
  busyRef.current = isLoading || isLoadingMore;

  useEffect(() => {
    if (!enabled || !hasNextPage) return undefined;

    const sentinel = sentinelRef?.current;
    if (!sentinel) return undefined;

    let root = null;
    for (const selector of scrollRootSelectors) {
      const el = sentinel.closest(selector);
      if (el) {
        root = el;
        break;
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !busyRef.current) {
          onLoadMoreRef.current?.();
        }
      },
      { root, rootMargin: "120px", threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    enabled,
    hasNextPage,
    isLoading,
    isLoadingMore,
    sentinelRef,
    scrollRootSelectors,
  ]);
}
